import { useParams, useOutletContext, Navigate } from "react-router-dom";
import { usePage } from "@/hooks/useSite";
import PageRenderer from "@/components/site/PageRenderer";
import Seo from "@/components/Seo";

const REDIRECTS = { menus: "/cuisine" };

export default function Page() {
  const { slug = "home" } = useParams();
  const { settings, env, base } = useOutletContext();
  const page = usePage(slug, env);

  if (REDIRECTS[slug]) return <Navigate to={`${base}${REDIRECTS[slug]}`} replace />;

  if (page === null) {
    return <div className="flex h-screen items-center justify-center" data-testid="page-loading"><p className="font-serif text-xl text-gold animate-pulse">Plating…</p></div>;
  }
  if (page === false) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4" data-testid="page-not-found">
        <h1 className="font-serif text-5xl text-ink">Off the Menu</h1>
        <p className="text-ink2">This page doesn't exist yet{env === "staging" ? " — has it been published?" : ""}.</p>
      </div>
    );
  }
  const brand = settings?.brand_name || "Chef Blaine Guillot";
  const heroImg = (page.hero_slides || []).find((s) => s.image)?.image;
  const faq = (page.sections || []).find((s) => s.type === "faq");
  const jsonLd = faq
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (faq.items || []).map((i) => ({ "@type": "Question", name: i.q, acceptedAnswer: { "@type": "Answer", text: i.a } })),
      }
    : slug === "home"
      ? {
          "@context": "https://schema.org",
          "@type": "FoodEstablishment",
          name: brand,
          description: page.seo_description || settings?.tagline,
          url: window.location.origin,
          servesCuisine: ["Cajun", "Creole", "Southern", "Seafood"],
          areaServed: "South Louisiana",
          ...(settings?.notification_email ? { email: settings.notification_email } : {}),
          ...(settings?.socials?.facebook ? { sameAs: [settings.socials.facebook] } : {}),
        }
      : undefined;

  return (
    <>
      <Seo
        title={slug === "home" ? `${brand} | South Louisiana Catering & Private Chef` : `${page.title} — ${brand}`}
        siteName={brand}
        description={page.seo_description || settings?.tagline}
        image={heroImg}
        jsonLd={jsonLd}
      />
      <PageRenderer page={page} settings={settings} env={env} base={base} />
    </>
  );
}
